import { createTheme, Theme } from "@mui/material/styles";

// Raw design-system scale values (see src/styles/tokens.css).
// Duplicated here so MUI can consume them at theme-build time.
// Both light and dark themes are built from this same scale — dark mode only
// swaps *which* steps land in which semantic slot.
export const tokens = {
  night: {
    50: "#eef1f7",
    100: "#d6dcea",
    200: "#aab5d0",
    300: "#7486ae",
    400: "#4a5d88",
    500: "#2d3e66",
    600: "#213054",
    700: "#182440",
    800: "#101a2f",
    900: "#0a1120",
    950: "#05080f",
  },
  sky: {
    50: "#f1f7fc",
    100: "#deecf7",
    200: "#b9d8ee",
    300: "#87bde0",
    400: "#55a0cf",
    500: "#2f87bf",
    600: "#216b9d",
    700: "#1a547b",
    800: "#144159",
    900: "#0e2e3f",
  },
  paper: {
    50: "#fbfaf6",
    100: "#f4f1e9",
    200: "#e8e3d3",
    300: "#d2cab3",
    400: "#a8a088",
    500: "#756e5b",
    600: "#504a3d",
    700: "#39342a",
    800: "#24201a",
    900: "#13110e",
  },
  term: {
    50: "#edf9ef",
    100: "#cdedd3",
    300: "#7fcb91",
    500: "#2f9d4e",
    600: "#207a3b",
    700: "#175a2c",
  },
  amber: {
    100: "#fbead0",
    300: "#f2c37a",
    500: "#db9023",
    700: "#8b5b14",
  },
  rust: {
    100: "#f7dcd3",
    300: "#e08873",
    500: "#c24d30",
    700: "#7d2d18",
  },
};

export type ThemeMode = "light" | "dark";

const fontSans = "'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif";
const fontSerif = "'IBM Plex Serif', ui-serif, Georgia, serif";
const fontMono = "'IBM Plex Mono', ui-monospace, 'SF Mono', Menlo, monospace";

// Derived palette: which scale step fills each semantic slot per mode.
const palette = (mode: ThemeMode) => {
  if (mode === "dark") {
    return {
      bgDefault: tokens.night[900],
      bgPaper: tokens.night[800],
      bgSunken: tokens.night[950],
      textPrimary: tokens.paper[50],
      textSecondary: tokens.paper[200],
      textDisabled: tokens.night[300],
      divider: "rgba(255, 255, 255, 0.08)",
      brandMain: tokens.sky[400],
      brandLight: tokens.sky[300],
      brandDark: tokens.sky[500],
      brandContrast: tokens.night[900],
      secondaryMain: tokens.sky[300],
      secondaryLight: tokens.sky[200],
      secondaryDark: tokens.sky[400],
      focusHalo: "rgba(135, 189, 224, 0.4)",
      outlinedHover: tokens.night[700],
      buttonHoverBg: "rgba(135, 189, 224, 0.12)",
    };
  }
  return {
    bgDefault: tokens.paper[50],
    bgPaper: "#ffffff",
    bgSunken: tokens.paper[100],
    textPrimary: tokens.paper[900],
    textSecondary: tokens.paper[700],
    textDisabled: tokens.paper[400],
    divider: tokens.paper[200],
    brandMain: tokens.night[500],
    brandLight: tokens.night[400],
    brandDark: tokens.night[700],
    brandContrast: tokens.paper[50],
    secondaryMain: tokens.sky[500],
    secondaryLight: tokens.sky[300],
    secondaryDark: tokens.sky[700],
    focusHalo: "rgba(47, 135, 191, 0.3)",
    outlinedHover: tokens.paper[100],
    buttonHoverBg: "rgba(45, 62, 102, 0.06)",
  };
};

export const makeTheme = (mode: ThemeMode): Theme => {
  const p = palette(mode);
  return createTheme({
    palette: {
      mode,
      primary: {
        light: p.brandLight,
        main: p.brandMain,
        dark: p.brandDark,
        contrastText: p.brandContrast,
      },
      secondary: {
        light: p.secondaryLight,
        main: p.secondaryMain,
        dark: p.secondaryDark,
        contrastText: mode === "dark" ? tokens.night[900] : "#ffffff",
      },
      success: {
        light: tokens.term[300],
        main: mode === "dark" ? tokens.term[300] : tokens.term[500],
        dark: tokens.term[700],
      },
      warning: {
        light: tokens.amber[300],
        main: mode === "dark" ? tokens.amber[300] : tokens.amber[500],
        dark: tokens.amber[700],
      },
      error: {
        light: tokens.rust[300],
        main: mode === "dark" ? tokens.rust[300] : tokens.rust[500],
        dark: tokens.rust[700],
      },
      info: {
        light: tokens.sky[300],
        main: mode === "dark" ? tokens.sky[300] : tokens.sky[500],
        dark: tokens.sky[700],
      },
      background: {
        default: p.bgDefault,
        paper: p.bgPaper,
      },
      text: {
        primary: p.textPrimary,
        secondary: p.textSecondary,
        disabled: p.textDisabled,
      },
      divider: p.divider,
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: fontSans,
      h1: { fontFamily: fontSerif, fontWeight: 500, fontSize: "4rem", lineHeight: 1.1, letterSpacing: "-0.02em" },
      h2: { fontFamily: fontSerif, fontWeight: 500, fontSize: "3rem", lineHeight: 1.15, letterSpacing: "-0.02em" },
      h3: { fontFamily: fontSans, fontWeight: 600, fontSize: "2.25rem", lineHeight: 1.2 },
      h4: { fontFamily: fontSans, fontWeight: 600, fontSize: "1.75rem", lineHeight: 1.25 },
      h5: { fontFamily: fontSans, fontWeight: 600, fontSize: "1.375rem", lineHeight: 1.3 },
      h6: { fontFamily: fontSans, fontWeight: 600, fontSize: "1.125rem", lineHeight: 1.3 },
      subtitle1: { fontFamily: fontSans, fontSize: "1.125rem" },
      subtitle2: { fontFamily: fontSans, fontSize: "0.875rem", fontWeight: 500 },
      body1: { fontFamily: fontSans, fontSize: "1rem", lineHeight: 1.55 },
      body2: { fontFamily: fontSans, fontSize: "0.875rem", lineHeight: 1.55 },
      button: { fontFamily: fontSans, fontWeight: 500, textTransform: "none", letterSpacing: 0 },
      caption: { fontFamily: fontSans, fontSize: "0.75rem" },
      overline: {
        fontFamily: fontMono,
        fontWeight: 500,
        fontSize: "0.75rem",
        letterSpacing: "0.12em",
        textTransform: "uppercase",
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            boxShadow: "none",
            fontWeight: 500,
            "&:hover": { boxShadow: "none" },
          },
          containedPrimary: {
            color: p.brandContrast,
            "&:hover": { backgroundColor: p.brandDark },
            "&:active": { backgroundColor: p.brandDark },
          },
          outlinedPrimary: {
            borderColor: p.divider,
            color: p.brandMain,
            "&:hover": {
              borderColor: p.brandMain,
              backgroundColor: p.outlinedHover,
            },
          },
          text: {
            "&:hover": { backgroundColor: p.buttonHoverBg },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: p.bgPaper,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { color: p.textPrimary },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: mode === "dark" ? tokens.sky[300] : tokens.sky[600],
            textDecorationColor:
              mode === "dark"
                ? "rgba(135, 189, 224, 0.4)"
                : "rgba(33, 107, 157, 0.4)",
            textUnderlineOffset: "0.18em",
            "&:hover": {
              color: mode === "dark" ? tokens.sky[200] : tokens.sky[700],
              textDecorationColor: "currentColor",
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: p.bgDefault },
          "::selection": {
            background: mode === "dark" ? tokens.sky[700] : tokens.sky[200],
            color: mode === "dark" ? tokens.paper[50] : tokens.night[900],
          },
        },
      },
    },
  });
};

// Default export preserved for anywhere still importing the old singleton.
const theme = makeTheme("light");
export default theme;
